/*
 * The inconsistency / audit report for a run (U25).
 *
 * The customer-facing "what really happened on Meta" report: independent
 * re-verification of every finalized ticket, surfaced as the eight audit
 * verdicts. Discrepancies are grouped by verdict with counts and each row's
 * recommended recovery action; a clean run shows the all-verified summary; a
 * `recheck_recommended` flag raises the "re-audit in ~1h" banner.
 *
 * Data comes from the shared `useRunInconsistencies(runId)` query via the
 * co-located `useVerdictReport` hook (which groups + shapes it) — the shared
 * `src/api/queries.ts` is intentionally left untouched.
 */

import type { ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert, Button, Group, Stack, Text, Title } from "@mantine/core";
import { errorMessage } from "../api/errors";
import {
  CleanSummary,
  RecheckBanner,
  ReportSummary,
  VerdictGroup,
  useVerdictReport,
} from "../components/verdicts";

/** The page title block (Mantine `Group`/`Title`/`Text`). */
function PageHeader({
  title,
  description,
  actions,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Group justify="space-between" align="flex-start" wrap="nowrap" mb="lg">
      <Stack gap={4}>
        <Title order={1} fz="h2">
          {title}
        </Title>
        {description && (
          <Text c="dimmed" fz="sm">
            {description}
          </Text>
        )}
      </Stack>
      {actions && <div>{actions}</div>}
    </Group>
  );
}

/** A titled empty/loading state (Mantine `Stack`/`Text`). */
function EmptyState({
  title,
  description,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <Stack align="center" gap="sm" py="xl" px="md" ta="center">
      {icon && (
        <Text aria-hidden="true" fz={28} lh={1}>
          {icon}
        </Text>
      )}
      <Text fw={600} fz="lg">
        {title}
      </Text>
      {description && (
        <Text c="dimmed" fz="sm" maw={420}>
          {description}
        </Text>
      )}
    </Stack>
  );
}

/** Monospace inline cell helper for ids. */
function Mono({ children }: { children: ReactNode }) {
  return (
    <Text component="span" ff="monospace" fz="sm">
      {children}
    </Text>
  );
}

function ReportBody({ runId }: { runId: string }) {
  const { view, isPending, isError, error, refetch } = useVerdictReport(runId);

  if (isPending) {
    return (
      <EmptyState
        icon="◷"
        title="Loading report…"
        description="Re-verifying this run's tickets against Meta."
      />
    );
  }

  if (isError || !view) {
    return (
      <Alert color="red" variant="light" role="alert">
        Couldn&apos;t load the inconsistency report — {errorMessage(error)}{" "}
        <Button variant="subtle" color="gray" size="xs" onClick={() => void refetch()}>
          Retry
        </Button>
      </Alert>
    );
  }

  const resolvedCount = view.groups.reduce(
    (acc, g) => acc + g.rows.filter((r) => r.resolved).length,
    0,
  );

  return (
    <Stack gap="lg">
      {view.recheckRecommended && <RecheckBanner />}

      {view.clean ? (
        <CleanSummary />
      ) : (
        <>
          <ReportSummary
            groupCount={view.groups.length}
            totalDiscrepancies={view.totalDiscrepancies}
            resolvedCount={resolvedCount}
          />
          {view.groups.map((group) => (
            <VerdictGroup
              key={group.classification}
              classification={group.classification}
              rows={group.rows}
              runId={runId}
            />
          ))}
        </>
      )}
    </Stack>
  );
}

export function RunReportPage() {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return (
      <EmptyState
        icon="❑"
        title="No run selected"
        description="Open a run to see its inconsistency report."
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Inconsistency report"
        description={
          <>
            What really happened on Meta for run <Mono>{id}</Mono>.
          </>
        }
        actions={
          <Text
            component={Link}
            to={`/runs/${id}`}
            fz="sm"
            fw={500}
            c="blue"
            td="none"
          >
            ← Back to run
          </Text>
        }
      />
      <ReportBody runId={id} />
    </>
  );
}
